import React, { memo, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { Card } from 'transfix-ui/components/Card'
import Typography from 'transfix-ui/core/Typography'
import { makeStyles } from 'transfix-ui/core/styles'
import { brandColors } from 'transfix-ui/theme/colors'
import { IShipmentMissingInfo, Maybe } from 'types/FMSGraphqlTypes'
import ShipmentLane, { IShipmentLaneProps } from 'apps/fleet-planner/components/ShipmentLane'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import MissingInformation from 'apps/fleet-planner/pages/loads-needing-attention/components/MissingInformation'
import { TEST_IDS } from '../../constants'

const useShipmentNeedingAttentionRowStyles = makeStyles(theme => ({
  card: {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: brandColors.coolGray1,
      cursor: 'pointer',
    },
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),

    [theme.breakpoints.up('lg')]: {
      justifyContent: 'space-between',
      alignItems: 'center',
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      position: 'relative',
    },
  },
  shipmentIdContainer: {
    order: 1,
    [theme.breakpoints.up('lg')]: {
      flex: `0 0 100%`,
    },
  },
  shipmentId: {
    letterSpacing: '1.5px',
  },
  shipmentLane: {
    order: 3,
    [theme.breakpoints.up('lg')]: {
      order: 2,
    },
  },
  missingInfo: {
    order: 2,
    [theme.breakpoints.up('lg')]: {
      order: 3,
      position: 'absolute',
      right: 0,
      top: '50%',
      transform: 'translateY(-50%)',
    },
  },
}))

export interface IShipmentNeedingAttentionRowProps {
  id: Maybe<string>
  marketplaceId: Maybe<string>
  /**
   * @default []
   */
  stops: IShipmentLaneProps['stops']
  /**
   * @default []
   */
  missingInformation: Maybe<IShipmentMissingInfo[]>
  /**
   * @default null
   */
  missingInfoMessage: Maybe<string>
}

const ShipmentNeedingAttentionRow = memo<IShipmentNeedingAttentionRowProps>(
  ({ missingInformation = [], id, stops = [], missingInfoMessage = null, marketplaceId }) => {
    const { trackEvent } = useFMSAnalytics()
    const history = useHistory()
    const classes = useShipmentNeedingAttentionRowStyles()

    const cardClickHandler = useCallback(() => {
      trackEvent('Loads Needing Attention', 'CLICKED_LOAD_NEEDING_ATTENTION_ROW', {
        shipmentId: marketplaceId,
        missingInformation,
      })
      history.push(`/loads-needing-attention/${id}/details`)
    }, [history, id, marketplaceId, missingInformation, trackEvent])

    return (
      <div onClick={cardClickHandler} role='button' tabIndex={0} aria-hidden='true'>
        <Card className={classes.card} dataTest={TEST_IDS.row}>
          <div className={classes.root}>
            <div className={classes.shipmentIdContainer}>
              <Typography
                variant='caption'
                color='textSecondary'
                component='div'
                className={classes.shipmentId}>
                #{marketplaceId}
              </Typography>
            </div>
            <div className={classes.shipmentLane}>
              <ShipmentLane stops={stops} />
            </div>
            <div className={classes.missingInfo}>
              {missingInformation && (
                <MissingInformation
                  missingInformation={missingInformation}
                  message={missingInfoMessage}
                />
              )}
            </div>
          </div>
        </Card>
      </div>
    )
  }
)

export default ShipmentNeedingAttentionRow
